import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import ModalModern from "../ModalModern";
import ModernPopupHeader from "../common/ModernPopupHeader";
import ModernPopupBody from "../common/ModernPopupBody";
import MultiSelect from "../../forms/MultiSelect";
import FieldLabel from "../../forms/FieldLabel";
import NotificationAlert from "../../alerts/notifications/NotificationAlert";

/**
 * CreateCreditRuleModal Component
 *
 * Modal UI component responsible for capturing user parameters to configure or update
 * a Credit Safeguard rule. Displays only connected credit accounts.
 *
 * @component
 */
function CreateCreditRuleModal({
  content,
  onClose,
  onSave,
  metadata = [],
  apiError = "",
  isSubmitting = false,
  onClearApiError,
  ruleToEdit = null,
}) {
  const copy = content.subscriber_view.money_guardian.modals.create_edit_modal;
  const creditCopy = content.subscriber_view.money_guardian.credit_safeguards;
  const isEditMode = !!ruleToEdit;

  // Initialize state using ruleToEdit if it exists
  const [formData, setFormData] = useState({
    name: ruleToEdit ? ruleToEdit.name : "",
    description: ruleToEdit ? ruleToEdit.description : "",
    amount: ruleToEdit ? String(ruleToEdit.amount) : "",
    isEnable: ruleToEdit ? ruleToEdit.isEnable : true,

    accounts:
      ruleToEdit && ruleToEdit.accounts
        ? ruleToEdit.accounts.map((acc) => String(acc.uuid || acc.id || acc))
        : [],
  });

  // Map credit accounts safely across all institutions matching acc.type === "credit"
  const accountOptions = useMemo(() => {
    const safeMetadata = metadata || [];
    const fallbackAccount = copy.account_fallback_label;
    const fallbackInstitution = copy.institution_fallback_label;

    return safeMetadata.flatMap((inst) =>
      (inst.accounts || [])
        .filter((acc) => {
          const accType = (acc.type || "").toLowerCase();
          return accType === "credit" || accType === "credit card";
        })
        .map((acc) => ({
          label: `${acc.name || fallbackAccount} (${
            inst.institution_name || fallbackInstitution
          })`,
          value: String(acc.uuid || acc.id || acc.account_id),
        })),
    );
  }, [metadata, copy]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAccountsChange = (selectedValues) => {
    setFormData((prev) => ({
      ...prev,
      accounts: selectedValues,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      amount: parseFloat(formData.amount),
    });
  };

  return (
    <ModalModern onClose={onClose}>
      <ModernPopupHeader onClose={onClose}>
        <div className="header-content-modern-row">
          <h3 className="transaction-popup-title">
            {isEditMode
              ? "Update Credit Safeguard Rule"
              : "Create Credit Safeguard Rule"}
          </h3>
        </div>
      </ModernPopupHeader>

      <ModernPopupBody>
        <form
          onSubmit={handleSubmit}
          onFocus={() => apiError && onClearApiError?.()}
          className="mp-col-container"
        >
          {apiError && (
            <NotificationAlert
              alertType="error"
              alertTitle={
                isEditMode ? copy.alert_fail_edit : copy.alert_fail_create
              }
              alertDescription={apiError}
            />
          )}

          <div
            className="mp-col-container mobile-to-col"
            style={{ width: "100%", padding: "0" }}
          >
            <div
              className="mp-transacion-metadata"
              style={{ width: "100%", padding: "0" }}
            >
              {/* Row 1: Name and Trigger Amount Threshold */}
              <div
                className="mp-row-container mobile-to-col"
                style={{ gap: "20px", padding: "0", width: "100%" }}
              >
                <div className="form-field">
                  <p>{copy.field_name}</p>
                  <input
                    type="text"
                    name="name"
                    className="form-input"
                    placeholder="e.g., Credit Card High Balance Alert"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-field">
                  <p>Limit Amount</p>
                  <input
                    type="number"
                    name="amount"
                    className="form-input"
                    placeholder={copy.field_amount_placeholder}
                    min="0"
                    step="0.01"
                    value={formData.amount}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              {/* Row 2: Rule Description */}
              <div className="form-field">
                <p>{copy.field_description}</p>
                <textarea
                  name="description"
                  className="form-input"
                  placeholder="Describe trigger threshold parameters for this credit safeguard..."
                  value={formData.description}
                  onChange={handleChange}
                  style={{ height: "70px", resize: "none" }}
                />
              </div>

              {/* Row 3: Scoped MultiSelect Filters Block */}
              <div
                className="mp-col-container full-width ten-gap"
                style={{ marginTop: "10px" }}
              >
                <p
                  style={{
                    fontWeight: 500,
                    margin: "0 0 4px 0",
                    color: "#374151",
                  }}
                >
                  {copy.filters_heading}
                </p>

                <div className="form-field">
                  <MultiSelect
                    options={accountOptions}
                    value={formData.accounts}
                    onChange={handleAccountsChange}
                    placeholder="All Connected Credit Cards"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Actions Footer */}
          <div
            className="mp-row-container align-center"
            style={{ marginTop: "24px", padding: "0" }}
          >
            <button
              type="submit"
              className="cta-button secondary"
              disabled={isSubmitting}
              style={{ width: "160px" }}
            >
              {isSubmitting
                ? copy.cta_submitting
                : isEditMode
                ? copy.cta_update
                : copy.cta_save}
            </button>
          </div>
        </form>
      </ModernPopupBody>
    </ModalModern>
  );
}

CreateCreditRuleModal.propTypes = {
  content: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  metadata: PropTypes.arrayOf(PropTypes.object),
  apiError: PropTypes.string,
  isSubmitting: PropTypes.bool,
  onClearApiError: PropTypes.func,
  ruleToEdit: PropTypes.shape({
    uuid: PropTypes.string,
    name: PropTypes.string,
    amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    description: PropTypes.string,
    isEnable: PropTypes.bool,
    accounts: PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    ),
  }),
};

export default CreateCreditRuleModal;
